import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowRight, Check } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contacts", form);
      setDone(true);
      toast.success("Message sent — we'll be in touch.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[#0A0A0C] text-white py-20">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">Contact</div>
          <h1 className="font-serif-display text-5xl lg:text-7xl leading-[0.95] tracking-tight">
            Let's talk.
          </h1>
          <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
            Press, partnerships, large gifts, or rescue coordination — reach out and a real human
            will respond within 48 hours.
          </p>
          <div className="mt-12 space-y-6 text-white/80">
            <div className="flex items-center gap-4">
              <Mail size={18} className="text-[#E85D04]" />
              <span>hello@jreardonfoundation.com</span>
            </div>
            <div className="flex items-center gap-4">
              <Phone size={18} className="text-[#E85D04]" />
              <span>+1 (415) 555-0144</span>
            </div>
            <div className="flex items-center gap-4">
              <MapPin size={18} className="text-[#E85D04]" />
              <span>HQ — San Francisco, CA · Field offices worldwide</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="border border-white/10 bg-[#121215] p-10 text-center">
              <Check size={48} className="mx-auto text-[#E85D04]" />
              <h2 className="mt-6 font-serif-display text-4xl">Message received.</h2>
              <p className="mt-3 text-white/60">Expect a reply within 48 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-white/10 bg-[#121215] p-8 lg:p-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name *">
                  <Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
                </Field>
                <Field label="Email *">
                  <Input type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
                </Field>
                <Field label="Subject" className="md:col-span-2">
                  <Input value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
                </Field>
                <Field label="Message *" className="md:col-span-2">
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={6}
                    data-testid="contact-message"
                    className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04] resize-none"
                  />
                </Field>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-8 inline-flex items-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"} <ArrowRight size={14} />
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] tracking-[0.3em] uppercase text-white/50 mb-2">{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = "text", testid }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-[#0A0A0C] border border-white/10 px-4 py-3 outline-none focus:border-[#E85D04]"
    />
  );
}
